import { useTranslation } from "react-i18next";
import ErrorNotice from "../../../../errors/ErrorNotice";
import { parseThreadEntryRef } from "../../../../shared/references/threadReferences";
import { useSubmission } from "../../../../shared/state/useSubmission";
import AppButton from "../../../../ui/buttons/AppButton";
import LoadStatus from "../../../../ui/feedback/LoadStatus";
import StatusMessage from "../../../../ui/feedback/StatusMessage";
import { FORM_HELP_TEXT_CLASS } from "../../../../ui/styles/formStyles";
import PassageList from "../../../memories/keep/PassageList";
import { suggestedMemoryTitle } from "../../../memories/keep/memoryTitle";
import type { PreparationViewProps } from "../preparationView";
import { useShippedWorkflow } from "../shippedWorkflow";
import { startKeepMemory } from "../startCommands";
import type { MemoryIntent } from "../threadIntent";

/**
 * Keeping passages as a memory, prepared beside their Thread (ADR-0031 D5, MEM-2): the
 * passages, then Start, which makes the memory's own small Thread of the library's "Keep a
 * memory". Its step holds the keep form; the memory is the keeper's own and active at once
 * (J-01 (a)). Preparing writes nothing.
 */
export default function MemoryPreparation({ intent, requestKey, onStarted }: PreparationViewProps<MemoryIntent>) {
  const { t } = useTranslation();
  const shipped = useShippedWorkflow(intent.projectId, "keep_memory");
  const { state: submission, submit } = useSubmission();
  const starting = submission.status === "submitting" || submission.status === "succeeded";
  const title = t("threads.prepare.memory.threadTitle", { title: suggestedMemoryTitle(intent.passages[0]?.quote ?? "") });

  if (shipped.state.status !== "loaded") return <LoadStatus state={shipped.state} loadingLabel={t("common.loading")} onRetry={shipped.reload} />;
  const { workflow, contentVersion } = shipped.state.value;
  const start = () => {
    if (workflow === null) return;
    submit(async () => {
      const thread = await startKeepMemory({
        projectId: intent.projectId,
        title,
        workflow: workflow.reference,
        contentVersion,
        originThreadId: intent.threadId,
        passages: intent.passages,
        requestKey,
      });
      onStarted(thread.threadId);
    });
  };

  return (
    <div className="space-y-4">
      <p className={FORM_HELP_TEXT_CLASS}>{t("memories.keep.explanation")}</p>
      <PassageList passages={intent.passages.map((passage) => ({ key: `${passage.entry}:${String(passage.occurrence)}:${passage.quote}`, quote: passage.quote, entry: parseThreadEntryRef(passage.entry)?.number ?? passage.entry }))} />
      {intent.skipped === 0 ? null : <p className="app-status-text-warning text-xs">{t("memories.keep.skipped", { count: intent.skipped })}</p>}
      {workflow === null ? (
        <StatusMessage tone="warning" title={t("threads.prepare.memory.noWorkflowTitle")}>
          <p>{t("threads.prepare.memory.noWorkflowBody")}</p>
        </StatusMessage>
      ) : (
        <p className={FORM_HELP_TEXT_CLASS}>{t("threads.prepare.memory.how", { workflow: workflow.name })}</p>
      )}
      {submission.status === "failed" ? <ErrorNotice error={submission.error} /> : null}
      <div className="flex justify-end">
        <AppButton variant="primary" size="sm" onClick={start} disabled={starting || workflow === null || intent.passages.length === 0}>
          {starting ? t("threads.prepare.starting") : t("threads.prepare.run.start")}
        </AppButton>
      </div>
    </div>
  );
}
